import React, { useMemo, useState } from 'react';
import { Paper, Typography, Stack, FormControl, Select, MenuItem, Button, Box, OutlinedInput, Checkbox, ListItemText } from '@mui/material';
import Chart from 'react-apexcharts';
import { exportCsvWithDate } from './csvUtil';
import { showTooltip, hideTooltip } from './tooltipManager';

const GRAPH_HEIGHT = 360;
const BIN_SIZE = 100;
const MAX_SCORE = 1500;

function makeBins(binSize = BIN_SIZE, maxScore = MAX_SCORE) {
    const labels = [];
    for (let start = 0; start < maxScore; start += binSize) {
        const end = Math.min(start + binSize - 1, maxScore - 1);
        labels.push(`${start}-${end}`);
    }
    labels.push(`${maxScore}`);
    return labels;
}

const TeamDistributionChart = ({ eventsScores = {}, adjustedTeamsMap = {} }) => {
    const [valueType, setValueType] = useState('adjusted');
    const [selectedTeams, setSelectedTeams] = useState(() => {
        // default to the first 2 teams in the dropdown
        return Object.values(adjustedTeamsMap || {}).map(t => t.uid).slice(0, 2);
    });

    const teamScoresMap = useMemo(() => {
        const map = {};
        Object.keys(eventsScores || {}).forEach(eUid => {
            const ev = eventsScores[eUid];
            if (!ev || !Array.isArray(ev.scores)) return;
            (ev.scores || []).forEach(s => {
                const t = s.teamUid;
                if (!map[t]) map[t] = [];
                const val = valueType === 'adjusted' ? (s.finalScoreAssociated ?? s.finalScore) : (s.finalScore ?? 0);
                const n = Number(val || 0);
                if (!isNaN(n)) map[t].push(n);
            });
        });
        return map;
    }, [eventsScores, valueType]);

    const bins = useMemo(() => makeBins(BIN_SIZE, MAX_SCORE), []);

    const series = useMemo(() => {
        return selectedTeams.map(tUid => {
            const scores = teamScoresMap[tUid] || [];
            const counts = new Array(bins.length).fill(0);
            scores.forEach(s => {
                if (s >= MAX_SCORE) { counts[bins.length-1]++; return; }
                const idx = Math.floor(s / BIN_SIZE);
                counts[idx]++;
            });
            const tm = adjustedTeamsMap[tUid] || { teamNumber: '', teamName: tUid };
            const label = `${tm.teamNumber || ''} ${tm.teamName || ''}`.trim() || tUid;
            return { name: label, data: counts };
        });
    }, [selectedTeams, teamScoresMap, bins, adjustedTeamsMap]);

    const options = useMemo(() => ({
        chart: {
            id: 'team-distribution',
            toolbar: { show: true },
            animations: { enabled: false, dynamicAnimation: { enabled: false } },
            parentHeightOffset: 0,
            events: {
                dataPointMouseEnter: function(event, chartContext, { seriesIndex, dataPointIndex, w }) {
                    try {
                        const rect = (event && event.target && event.target.closest) ? (event.target.closest('path, rect') || event.target).getBoundingClientRect() : null;
                        const x = rect ? (rect.left + rect.width / 2) : event.clientX;
                        const y = rect ? rect.top : event.clientY;
                        const bin = (w && w.config && w.config.xaxis && w.config.xaxis.categories && w.config.xaxis.categories[dataPointIndex]) || '';
                        const val = (w && w.config && w.config.series && w.config.series[seriesIndex] && w.config.series[seriesIndex].data && w.config.series[seriesIndex].data[dataPointIndex]) || 0;
                        const html = `<div style=\"pointer-events:none;padding:8px;font-size:13px;\"><strong>${bin}</strong><div style=\"margin-top:6px\">Count: <strong>${val}</strong></div></div>`;
                        showTooltip({ html, x, y });
                    } catch (e) {}
                },
                dataPointMouseLeave: function() { hideTooltip(0); },
                mouseLeave: function() { hideTooltip(0); }
            }
        },
        plotOptions: { bar: { horizontal: false, columnWidth: '80%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: bins, labels: { rotate: -45 }, tooltip: { enabled: false }, crosshairs: { show: false } },
        yaxis: { title: { text: 'Count' } },
        grid: { padding: { top: 8, right: 8, bottom: 4, left: 4 } },
        tooltip: { enabled: false },
        states: { hover: { filter: { type: 'none' } } },
        fill: { opacity: 1, opacityOnHover: 1 }
    }), [bins]);

    const allTeams = useMemo(() => {
        const list = Object.values(adjustedTeamsMap || {}).map(t => ({ uid: t.uid, number: Number(t.teamNumber ?? Infinity), label: `${t.teamNumber || ''} ${t.teamName || ''}`.trim() }));
        list.sort((a, b) => {
            if (!Number.isNaN(a.number) && !Number.isNaN(b.number)) return a.number - b.number;
            return (a.label || '').localeCompare(b.label || '');
        });
        return list;
    }, [adjustedTeamsMap]);

    const handleTeamChange = (e) => setSelectedTeams(e.target.value);

    const handleExport = () => {
        const headers = ['bin','teamLabel','count'];
        const rows = [];
        series.forEach(s => s.data.forEach((count, i) => rows.push({ bin: bins[i], teamLabel: s.name, count })));
        exportCsvWithDate('team-distribution', headers, rows);
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1">Team Score Distributions (0–1500, buckets of 100)</Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 240 }}>
                        <Select multiple displayEmpty value={selectedTeams} onChange={handleTeamChange} input={<OutlinedInput />} renderValue={(selected) => selected.length ? `${selected.length} team(s)` : 'Select teams'}>
                            {allTeams.map(t => (
                                <MenuItem key={t.uid} value={t.uid}>
                                    <Checkbox checked={selectedTeams.indexOf(t.uid) > -1} />
                                    <ListItemText primary={t.label || t.uid} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl size="small">
                        <Select value={valueType} onChange={(e)=>setValueType(e.target.value)}>
                            <MenuItem value="adjusted">Adjusted</MenuItem>
                            <MenuItem value="original">Original</MenuItem>
                        </Select>
                    </FormControl>

                    <Button size="small" variant="outlined" onClick={handleExport}>Export CSV</Button>
                </Stack>
            </Stack>

            <Box sx={{ position: 'relative', height: GRAPH_HEIGHT, mt: 2 }}>
                <Chart type="bar" series={series} options={options} height={GRAPH_HEIGHT} />
            </Box>
        </Paper>
    );
};

export default TeamDistributionChart;
